import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { collection, addDoc, updateDoc, doc,
  increment, serverTimestamp, query, where,
  getDocs, getDoc, writeBatch } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1a1a1a',
      fontFamily: '-apple-system, sans-serif',
      '::placeholder': { color: '#aaa' }
    },
    invalid: { color: '#dc2626' }
  }
};

function calculateNextBillingDate(preferredDeliveryDay, frequency) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // One-time needs should be billed immediately in the next billing run
  if (frequency === 'one-time') {
    return new Date(Date.UTC(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      12, 0, 0
    ));
  }

  const safeDeliveryDay = Math.max(1, Math.min(28, Number(preferredDeliveryDay) || 1));
  const billingDay = Math.max(1, safeDeliveryDay - 5);

  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  const thisMonthBilling = new Date(Date.UTC(currentYear, currentMonth, billingDay, 12, 0, 0));

  if (today <= new Date(currentYear, currentMonth, billingDay)) {
    return thisMonthBilling;
  }

  const nextMonthFirst = new Date(Date.UTC(currentYear, currentMonth + 1, 1, 12, 0, 0));
  return nextMonthFirst;
}

function CheckoutForm() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { cartItems, clearCart, removeFromCart, cartTotal } = useCart();
  const stripe = useStripe();
  const elements = useElements();
  const [adopting, setAdopting] = useState(false);
  const [adopted, setAdopted] = useState(false);
  const [error, setError] = useState('');
  const [adoptedCount, setAdoptedCount] = useState(0);
  const [cardOnFile, setCardOnFile] = useState(null);
  const [showNewCard, setShowNewCard] = useState(false);
  const [loadingCard, setLoadingCard] = useState(true);

  const priorityColors = {
    critical: { bg: '#fee2e2', color: '#991b1b' },
    core: { bg: '#fef3c7', color: '#92400e' },
    supplemental: { bg: '#e0f2fe', color: '#075985' }
  };

  useEffect(() => {
    async function checkCardOnFile() {
      try {
        const donorDoc = await getDoc(doc(db, 'donors', auth.currentUser.uid));
        const data = donorDoc.data();
        if (data.cardLast4) {
          setCardOnFile({ last4: data.cardLast4, brand: data.cardBrand });
        } else {
          setShowNewCard(true);
        }
      } catch (err) {
        console.error('Error checking card:', err);
        setShowNewCard(true);
      } finally {
        setLoadingCard(false);
      }
    }
    checkCardOnFile();
  }, []);

  async function handleCheckout() {
  if (cartItems.length === 0) return;

  const cartSnapshot = [...cartItems];

  try {
    setError('');
    setAdopting(true);

    // Handle card capture if new card
    if (showNewCard || !cardOnFile) {
      if (!stripe || !elements) {
        setError('Payment system not ready. Please try again.');
        setAdopting(false);
        return;
      }

      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: `${userProfile.firstName} ${userProfile.lastName}`,
          email: userProfile.email
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        setAdopting(false);
        return;
      }

      const functions = getFunctions();
      const savePaymentMethod = httpsCallable(functions, 'savePaymentMethod');
      const result = await savePaymentMethod({ paymentMethodId: paymentMethod.id });

      if (!result.data.success) {
        setError('Failed to save payment method. Please try again.');
        setAdopting(false);
        return;
      }
    }

    const batch = writeBatch(db);
    let successCount = 0;

    for (const need of cartSnapshot) {
      const existingQ = query(
        collection(db, 'subscriptions'),
        where('donorId', '==', auth.currentUser.uid),
        where('needId', '==', need.id),
        where('status', '==', 'active')
      );

      const existingSnap = await getDocs(existingQ);
      if (!existingSnap.empty) continue;

      const needSnap = await getDoc(doc(db, 'needs', need.id));
      const currentNeed = needSnap.exists() ? needSnap.data() : null;

      if (!currentNeed || currentNeed.status !== 'open') {
        console.warn('Skipping unavailable need:', need.id, need.itemName);
        continue;
      }

      const subRef = doc(collection(db, 'subscriptions'));

      batch.set(subRef, {
        donorId: auth.currentUser.uid,
        donorName: `${userProfile.firstName} ${userProfile.lastName}`,
        donorFirstName: userProfile.firstName,
        donorLastInitial: userProfile.lastName?.charAt(0) || '',
        donorCity: userProfile.city,
        donorState: userProfile.state,
        donorAnonymity: userProfile.anonymityPreference,

        orgId: need.orgId,
        orgName: need.orgName,
        needId: need.id,
        itemName: need.itemName,
        frequency: need.frequency,
        pricePerCycle: need.pricePerCycle,
        impactQuantity: need.impactQuantity,
        impactUnit: need.impactUnit,
        impactStatement: need.impactStatement,
        quantityPerCycle: need.quantityPerCycle || 1,
        vendorName: need.vendorName || '',
        vendorProductUrl: need.vendorProductUrl || '',

        status: 'active',
        orderStatus: 'not_due',
        createdAt: serverTimestamp(),
        nextBillingDate: calculateNextBillingDate(
  Number(need.preferredDeliveryDay || 1),
  need.frequency
),
        preferredDeliveryDay: Number(need.preferredDeliveryDay || 1),
        totalDeliveries: 0,
        totalImpact: 0
      });

      batch.update(doc(db, 'needs', need.id), {
        sponsorCount: increment(1),
        sponsorId: auth.currentUser.uid,
        sponsoredAt: serverTimestamp(),
        status: 'funded'
      });

      successCount++;
    }

    if (successCount === 0) {
      setError('None of the selected needs were available. Please refresh and try again.');
      setAdopting(false);
      return;
    }

    

await batch.commit();


const notificationPayload = {
  donorId: auth.currentUser.uid,
  donorName: `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim(),
  donorFirstName: userProfile.firstName || '',
  donorEmail: userProfile.email || '',
  donorCity: userProfile.city || '',
  donorState: userProfile.state || '',
  items: cartSnapshot.map(need => ({
    needId: need.id || '',
    orgId: need.orgId || '',
    orgName: need.orgName || '',
    itemName: need.itemName || '',
    frequency: need.frequency || '',
    pricePerCycle: Number(need.pricePerCycle || 0),
    impactStatement: need.impactStatement || '',
    nextBillingDate: calculateNextBillingDate(
      Number(need.preferredDeliveryDay || 1),
      need.frequency || ''
    ),
    preferredDeliveryDay: Number(need.preferredDeliveryDay || 1)
  })),
  createdAt: serverTimestamp(),
  status: 'pending'
};


try {
  const notificationRef = await addDoc(
    collection(db, 'checkoutNotifications'),
    notificationPayload
  );

  
} catch (notificationErr) {
    alert(`Notification write failed: ${notificationErr.message}`);
  throw notificationErr;
}




    setAdoptedCount(successCount);
    clearCart();
    setAdopted(true);
  } catch (err) {
    console.error('Checkout error:', err);
    setError('Something went wrong. Please try again.');
  } finally {
    setAdopting(false);
  }
}

  if (adopted) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb',
        fontFamily: '-apple-system, sans-serif', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        padding: '2rem' }}>
        <div style={{ background: 'white', borderRadius: '16px',
          padding: '3rem', maxWidth: '500px', width: '100%',
          textAlign: 'center', border: '1px solid #eee' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 700,
            marginBottom: '0.75rem' }}>
            You're now responsible for{' '}
            {adoptedCount} need{adoptedCount !== 1 ? 's' : ''}.
          </h1>
          <p style={{ color: '#555', lineHeight: 1.6,
            marginBottom: '2rem' }}>
            Each item will ship automatically on schedule.
            You will receive confirmation every time a box arrives.
          </p>
          <button onClick={() => navigate('/give/dashboard')}
            style={{ background: '#2d6a4f', color: 'white',
              border: 'none', padding: '0.875rem 2rem',
              borderRadius: '8px', fontSize: '1rem',
              cursor: 'pointer', fontWeight: 600,
              width: '100%', marginBottom: '0.75rem' }}>
            View my commitments
          </button>
          <button onClick={() => navigate('/give/browse')}
            style={{ background: 'transparent', color: '#2d6a4f',
              border: '2px solid #2d6a4f', padding: '0.875rem 2rem',
              borderRadius: '8px', fontSize: '1rem',
              cursor: 'pointer', fontWeight: 600, width: '100%' }}>
            Browse more needs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb',
      fontFamily: '-apple-system, sans-serif' }}>

      <header style={{ background: 'white',
        borderBottom: '1px solid #eee', padding: '0 2rem',
        height: '64px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', position: 'sticky',
        top: 0, zIndex: 100 }}>
        <div style={{ fontSize: '1.2rem', fontWeight: 700,
          color: '#2d6a4f', cursor: 'pointer' }}
          onClick={() => navigate('/')}>
          somebody.gives
        </div>
        <button onClick={() => navigate('/give/browse')}
          style={{ background: 'transparent', color: '#555',
            border: 'none', fontSize: '0.9rem', cursor: 'pointer' }}>
          ← Back to browse
        </button>
      </header>

      <main style={{ maxWidth: '640px', margin: '0 auto',
        padding: '2.5rem 2rem' }}>

        <h1 style={{ fontSize: '1.8rem', fontWeight: 700,
          marginBottom: '0.35rem' }}>
          Your cart
        </h1>
        <p style={{ color: '#555', marginBottom: '2rem' }}>
          Review your selections before taking responsibility.
        </p>

        {cartItems.length === 0 && (
          <div style={{ background: 'white', borderRadius: '12px',
            padding: '3rem', textAlign: 'center',
            border: '1px solid #eee' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🛒</div>
            <h2 style={{ marginBottom: '0.5rem' }}>Your cart is empty</h2>
            <p style={{ color: '#888', marginBottom: '1.5rem' }}>
              Browse open needs and add them to your cart.
            </p>
            <button onClick={() => navigate('/give/browse')}
              style={{ background: '#2d6a4f', color: 'white',
                border: 'none', padding: '0.75rem 1.5rem',
                borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              Browse needs
            </button>
          </div>
        )}

        {cartItems.length > 0 && (
          <>
            {cartItems.map(need => (
              <div key={need.id} style={{ background: 'white',
                borderRadius: '12px', padding: '1.25rem 1.5rem',
                border: '1px solid #eee', marginBottom: '0.75rem',
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '0.5rem',
                    marginBottom: '0.35rem', alignItems: 'center' }}>
                    <span style={{
                      background: priorityColors[need.priority]?.bg,
                      color: priorityColors[need.priority]?.color,
                      padding: '0.15rem 0.5rem', borderRadius: '20px',
                      fontSize: '0.72rem', fontWeight: 700,
                      textTransform: 'uppercase' }}>
                      {need.priority}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#888' }}>
                      {need.frequency}
                    </span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '1rem',
                    marginBottom: '0.2rem' }}>
                    {need.itemName}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    {need.orgName}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#555',
                    marginTop: '0.2rem' }}>
                    {need.impactStatement}
                  </div>
                </div>
                <div style={{ textAlign: 'right', minWidth: '100px',
                  flexShrink: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem',
                    color: '#2d6a4f', marginBottom: '0.35rem' }}>
                    ${Math.ceil(need.pricePerCycle)}
                    <span style={{ fontSize: '0.8rem', fontWeight: 400,
                      color: '#888' }}>/{need.frequency}</span>
                  </div>
                  <button onClick={() => removeFromCart(need.id)}
                    style={{ background: 'transparent', color: '#dc2626',
                      border: 'none', fontSize: '0.8rem',
                      cursor: 'pointer', padding: 0 }}>
                    Remove
                  </button>
                </div>
              </div>
            ))}

            {/* Payment section */}
            <div style={{ background: 'white', borderRadius: '12px',
              padding: '1.5rem', border: '1px solid #eee',
              marginTop: '1rem' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between',
                marginBottom: '0.5rem', fontSize: '0.9rem', color: '#555' }}>
                <span>{cartItems.length} commitment
                  {cartItems.length !== 1 ? 's' : ''}</span>
                <span>${Math.ceil(cartTotal)}/month total</span>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#888',
                marginBottom: '1.25rem' }}>
                Billed automatically. Cancel any commitment anytime
                from your dashboard.
              </div>

              {/* Card section */}
              {loadingCard ? (
                <div style={{ fontSize: '0.9rem', color: '#888',
                  marginBottom: '1rem' }}>
                  Loading payment info...
                </div>
              ) : cardOnFile && !showNewCard ? (
                <div style={{ background: '#f5f5f5', borderRadius: '8px',
                  padding: '0.875rem 1rem', marginBottom: '1rem',
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center' }}>
                  <span style={{ fontSize: '0.9rem', color: '#333' }}>
                    {cardOnFile.brand.charAt(0).toUpperCase() +
                      cardOnFile.brand.slice(1)} ending in {cardOnFile.last4}
                  </span>
                  <button onClick={() => setShowNewCard(true)}
                    style={{ background: 'transparent', color: '#2d6a4f',
                      border: 'none', fontSize: '0.85rem',
                      cursor: 'pointer', fontWeight: 600 }}>
                    Change
                  </button>
                </div>
              ) : (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.85rem', color: '#555',
                    display: 'block', marginBottom: '0.5rem' }}>
                    Card details
                  </label>
                  <div style={{ border: '1px solid #ddd',
                    borderRadius: '8px', padding: '0.875rem 1rem',
                    background: 'white' }}>
                    <CardElement options={CARD_ELEMENT_OPTIONS} />
                  </div>
                  {cardOnFile && (
                    <button onClick={() => setShowNewCard(false)}
                      style={{ background: 'transparent', color: '#888',
                        border: 'none', fontSize: '0.8rem',
                        cursor: 'pointer', marginTop: '0.5rem' }}>
                      ← Use saved card
                    </button>
                  )}
                </div>
              )}

              {error && (
                <div style={{ background: '#fee2e2', color: '#991b1b',
                  padding: '0.75rem', borderRadius: '8px',
                  marginBottom: '1rem', fontSize: '0.9rem' }}>
                  {error}
                </div>
              )}

              <button onClick={handleCheckout}
                disabled={adopting}
                style={{ width: '100%', background: '#1e3a5f',
                  color: 'white', border: 'none', padding: '1rem',
                  borderRadius: '8px', fontSize: '1rem',
                  cursor: 'pointer', fontWeight: 700 }}>
                {adopting
                  ? 'Setting up your commitments...'
                  : `Take responsibility for all ${cartItems.length} — $${Math.ceil(cartTotal)}/mo`}
              </button>
              <div style={{ textAlign: 'center', fontSize: '0.8rem',
                color: '#888', marginTop: '0.75rem' }}>
                By confirming you authorize recurring charges.
                A small platform fee is included in prices shown.
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function CartCheckout() {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm />
    </Elements>
  );
}

export default CartCheckout;